import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuthContext } from "@/contexts/AuthContext";
import { googleAuthApi, friendlyGoogleError } from "@/api/googleAuthApi";
import { GoogleGLogo } from "./GoogleGLogo";
import { TotpChallengeStep } from "./TotpChallengeStep";
import heroInboxImage from "@/assets/images/hero-inbox.jpg";
import "./LoginPage.css";

function postLoginPath(location) {
  const from = location.state?.from;
  if (from && typeof from === "object" && typeof from.pathname === "string") {
    const p = from.pathname;
    if (p && p !== "/login") {
      return `${p}${from.search || ""}${from.hash || ""}`;
    }
  }
  return "/";
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { login, verifyTotpLogin, isAuthenticated, isInitializing } = useAuthContext();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState("");
  const [forgotOpen, setForgotOpen] = useState(false);
  const [totpSession, setTotpSession] = useState("");

  useEffect(() => {
    const code = searchParams.get("google_error");
    if (code) {
      setError(friendlyGoogleError(code));
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const goAfterAuth = useCallback(() => {
    navigate(postLoginPath(location), { replace: true });
  }, [navigate, location]);

  useEffect(() => {
    if (!isInitializing && isAuthenticated) {
      goAfterAuth();
    }
  }, [isAuthenticated, isInitializing, goAfterAuth]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const outcome = await login({ email, password, rememberMe });
      if (outcome?.status === "totp_required") {
        setTotpSession(outcome.totp_session);
        return;
      }
      goAfterAuth();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleTotpSubmit = async (code) => {
    setLoading(true);
    setError("");
    try {
      await verifyTotpLogin({ totp_session: totpSession, code, rememberMe });
      goAfterAuth();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid authenticator code.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    setError("");
    try {
      const { redirect_url: redirectUrl } = await googleAuthApi.startLogin(
        email.trim() || undefined
      );
      if (!redirectUrl) {
        throw new Error("Google sign-in is unavailable.");
      }
      window.location.href = redirectUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Google sign-in failed.");
      setGoogleLoading(false);
    }
  };

  if (isInitializing) {
    return (
      <div className="login-page login-page--boot" role="status">
        <span className="login-page__boot-text">Loading…</span>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-page__left">
        <div className="login-page__main">
          <div className="login-page__card">
            <img
              className="login-page__brand login-page__brand--dark"
              src="/logo-light.png"
              alt="SentroDesk"
              width={168}
              decoding="async"
            />
            <img
              className="login-page__brand login-page__brand--light"
              src="/logo.png"
              alt="SentroDesk"
              width={168}
              decoding="async"
            />
            <h1 className="login-page__title">
              {totpSession ? "Two-factor authentication" : "Sign in to your workspace"}
            </h1>
            <p className="login-page__subtitle">
              {totpSession
                ? "Enter the 6-digit code from your authenticator app."
                : "Sign in with password or Google. After sign-in you can switch workspaces from the sidebar."}
            </p>

            {totpSession ? (
              <TotpChallengeStep
                onSubmit={handleTotpSubmit}
                onBack={() => {
                  setTotpSession("");
                  setError("");
                }}
                loading={loading}
                error={error}
              />
            ) : (
            <form className="login-page__form" onSubmit={handleSubmit} noValidate>
              {error ? (
                <div className="login-page__error" role="alert">
                  <i className="ri-error-warning-line" aria-hidden="true" />
                  <span>{error}</span>
                </div>
              ) : null}

              <div className="login-page__field">
                <label className="login-page__label" htmlFor="login-email">
                  Email
                </label>
                <div className="login-page__control-wrap">
                  <i className="ri-mail-line login-page__control-icon" aria-hidden="true" />
                  <input
                    id="login-email"
                    className="login-page__control"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    required
                    disabled={loading}
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="login-page__field">
                <div className="login-page__label-row">
                  <label className="login-page__label" htmlFor="login-password">
                    Password
                  </label>
                  <a
                    className="login-page__forgot"
                    href="/forgot-password"
                    onClick={(e) => {
                      e.preventDefault();
                      setForgotOpen(true);
                    }}
                  >
                    Forgot password?
                  </a>
                </div>
                <div className="login-page__control-wrap login-page__control-wrap--password">
                  <i className="ri-lock-2-line login-page__control-icon" aria-hidden="true" />
                  <input
                    id="login-password"
                    className="login-page__control"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Your password"
                    required
                    disabled={loading}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="login-page__password-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                    disabled={loading}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"} aria-hidden="true" />
                  </button>
                </div>
              </div>

              <label className="login-page__remember">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={loading}
                />
                <span className="login-page__remember-box" aria-hidden="true" />
                <span>Remember me</span>
              </label>

              <button type="submit" className="login-page__submit" disabled={loading || googleLoading}>
                {loading ? (
                  <>
                    <i className="ri-loader-4-line login-page__spin" aria-hidden="true" />
                    Signing in…
                  </>
                ) : (
                  "Login"
                )}
              </button>

              <div className="login-page__divider" role="separator">
                <span>or</span>
              </div>

              <button
                type="button"
                className="login-page__google"
                disabled={loading || googleLoading}
                onClick={handleGoogleSignIn}
              >
                {googleLoading ? (
                  <>
                    <i className="ri-loader-4-line login-page__spin" aria-hidden="true" />
                    Redirecting to Google…
                  </>
                ) : (
                  <>
                    <GoogleGLogo className="login-page__google-icon" />
                    Continue with Google
                  </>
                )}
              </button>
            </form>
            )}
          </div>
        </div>
      </div>

      <div className="login-page__right" aria-hidden="true">
        <div className="login-page__hero">
          <div className="login-page__pitch">
            <p className="login-page__pitch-eyebrow">AI-powered customer service platform</p>
            <p className="login-page__pitch-title">
              Good service.
              <br />
              Great conversations.
              <br />
              <em>One connected platform.</em>
            </p>
            <img
              className="login-page__pitch-shot"
              src={heroInboxImage}
              alt=""
              decoding="async"
              fetchPriority="low"
            />
          </div>
        </div>
      </div>

      {forgotOpen ? (
        <div
          className="login-page__modal-backdrop"
          role="presentation"
          onClick={() => setForgotOpen(false)}
        >
          <div
            className="login-page__modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="forgot-password-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="forgot-password-title" className="login-page__modal-title">
              Reset your password
            </h2>
            <p className="login-page__modal-body">
              We will help you reset your password. You will verify your email with a one-time code
              before choosing a new password.
            </p>
            <div className="login-page__modal-actions">
              <button
                type="button"
                className="login-page__modal-btn login-page__modal-btn--secondary"
                onClick={() => setForgotOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="login-page__modal-btn login-page__modal-btn--primary"
                onClick={() => {
                  setForgotOpen(false);
                  navigate("/forgot-password");
                }}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
